/**
 * アニメーション状態を管理するユーティリティ
 * ChatMessageコンポーネントでのタイピングアニメーションの
 * 状態を追跡・監視します
 */

// アニメーション状態を保持するマップ
const animations = new Map<number, {
  inProgress: boolean;  // アニメーション進行中フラグ
  completed: boolean;   // 完了フラグ
  startTime: number;    // 開始時間（デバッグ用）
}>();

// メッセージ追加キュー
type MessageAddCallback<T> = (message: T) => void;
interface QueuedMessage<T> {
  message: T;
  callback: MessageAddCallback<T>;
}
const messageQueue: QueuedMessage<any>[] = [];

/**
 * アニメーション開始を登録する
 * @param id アニメーション対象のメッセージID
 */
export const startAnimation = (id: number): void => {
  
  animations.set(id, {
    inProgress: true,
    completed: false,
    startTime: Date.now()
  });
};

/**
 * アニメーション完了を登録する
 * @param id アニメーション対象のメッセージID
 */
export const completeAnimation = (id: number): void => {
  
  const animation = animations.get(id);
  if (animation) {
    animation.inProgress = false;
    animation.completed = true;
    
    // アニメーション完了時に次のメッセージをキューから処理
    processNextQueuedMessage();
  } else {
    console.warn(`[AnimationManager] ID:${id}のアニメーション情報が見つかりません`);
  }
};

/**
 * アニメーション状態を取得する
 * @param id アニメーション対象のメッセージID
 */
export const getAnimationState = (id: number): { inProgress: boolean; completed: boolean } => {
  const animation = animations.get(id);
  if (animation) {
    return {
      inProgress: animation.inProgress,
      completed: animation.completed
    };
  }
  
  return {
    inProgress: false,
    completed: false
  };
};

/**
 * 進行中のアニメーションが存在するか確認
 */
export const hasOngoingAnimations = (): boolean => {
  let hasOngoing = false;
  animations.forEach((animation) => {
    if (animation.inProgress) {
      hasOngoing = true;
    }
  });
  return hasOngoing;
};

/**
 * アニメーション情報をクリアする
 * @param id アニメーション対象のメッセージID（指定しない場合は全て）
 */
export const clearAnimation = (id?: number): void => {
  if (id !== undefined) {
    animations.delete(id);
  } else {
    animations.clear();
  }
};

/**
 * 現在アニメーションが進行中かどうかを確認
 * @returns アニメーション進行中ならtrue
 */
export const isAnimationInProgress = (): boolean => {
  return hasOngoingAnimations();
};

/**
 * メッセージをキューに追加する
 * アニメーション完了後に順次処理される
 * 
 * @param message 追加するメッセージ
 * @param callback メッセージを追加するためのコールバック関数
 */
export const queueMessageAddition = <T>(message: T, callback: MessageAddCallback<T>): void => {
  
  // キューにメッセージを追加
  messageQueue.push({ message, callback });
  
  // アニメーション中でなければすぐに処理
  if (!isAnimationInProgress()) {
    processNextQueuedMessage();
  }
};

/**
 * キューから次のメッセージを取り出して処理する
 */
const processNextQueuedMessage = (): void => {
  // キューが空なら何もしない
  if (messageQueue.length === 0) {
    return;
  }
  
  // 現在アニメーション中なら処理しない
  if (isAnimationInProgress()) {
    return;
  }
  
  // キューから次のメッセージを取り出す
  const nextMessage = messageQueue.shift();
  if (nextMessage) {
    
    // コールバックを実行
    nextMessage.callback(nextMessage.message);
  }
};
